import React, { useState, useEffect, useRef } from 'react';
import { Settings, Power, Tag, Clock, Layers, Edit, Store, MapPin, Trash2, Copy } from 'lucide-react';
import { Service } from '../types/service';

interface ServiceCardProps {
  service: Service;
  onEdit: (service: Service) => void;
  onDuplicate: (service: Service) => void;
  onToggleActive: (service: Service) => void;
  onDelete: (service: Service) => void;
  categoryName?: string;
  professionalNames?: string[];
}

const ServiceCard: React.FC<ServiceCardProps> = ({
  service,
  onEdit,
  onDuplicate,
  onToggleActive,
  onDelete,
  categoryName,
  professionalNames = []
}) => {
  const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
  const actionsDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target as Node)) {
        setIsActionsDropdownOpen(false);
      }
    };

    if (isActionsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActionsDropdownOpen]);

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${mins}m`;
  };

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const isAddOn = (service as any).isAddOn || (service as any).is_add_on;
  const isQuotable = (service as any).quotable === true;
  const resolvedIcon = (service as any).icon || service.icon || service.category?.icon;
  // Handle both camelCase and snake_case for serviceLocation, normalize to uppercase
  const rawServiceLocation = service.serviceLocation || (service as any).service_location;
  const serviceLocation = rawServiceLocation 
    ? (typeof rawServiceLocation === 'string' ? rawServiceLocation.toUpperCase().trim() : 'IN_STORE')
    : 'IN_STORE';

  return (
    <div
      style={{ zIndex: isActionsDropdownOpen ? 1000 : undefined }}
      className={`rounded-lg shadow-sm border overflow-visible hover:shadow-md transition-all duration-200 relative flex flex-col h-full ${
      !service.isActive 
        ? 'bg-gray-50 border-gray-200' 
        : 'bg-white border-gray-200'
    }`}
    >
      {/* Actions Dropdown - Top Right Corner */}
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1" ref={actionsDropdownRef}>
        <button
          onClick={() => setIsActionsDropdownOpen(!isActionsDropdownOpen)}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors"
          title="Actions"
        >
          <Settings className="w-4 h-4" />
        </button>
        
        {isActionsDropdownOpen && (
          <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-[99999]">
            <div className="py-1">
              <button
                onClick={() => {
                  onEdit(service);
                  setIsActionsDropdownOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={() => {
                  onDuplicate(service);
                  setIsActionsDropdownOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Duplicate
              </button>
              <button
                onClick={() => {
                  onToggleActive(service);
                  setIsActionsDropdownOpen(false);
                }}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                  service.isActive
                    ? 'text-orange-600 hover:text-orange-700'
                    : 'text-green-600 hover:text-green-700'
                }`}
              >
                <Power className="w-4 h-4" />
                {service.isActive ? 'Deactivate' : 'Activate'}
              </button>
              <div className="border-t border-gray-100 my-1" />
              <button
                onClick={() => {
                  onDelete(service);
                  setIsActionsDropdownOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Service Content */}
      <div className={`p-5 flex flex-col flex-1 ${!service.isActive ? 'opacity-40 grayscale' : ''}`}>
        <div className="flex items-start gap-3 mb-4">
          {/* Service Icon */}
          <div className="flex-shrink-0 mt-0.5">
            <div 
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: '#3b82f615' }}
            >
              {resolvedIcon ? (
                <span className="text-xl" style={{ color: '#3b82f6' }}>
                  {resolvedIcon}
                </span>
              ) : (
                <i 
                  className="bi bi-star-fill"
                  style={{ 
                    fontSize: '20px',
                    color: '#FBBF24'
                  }}
                ></i>
              )}
            </div>
          </div>
          <div className="flex-1 min-w-0 pr-8">
            <h3 className="text-lg font-semibold text-gray-900 leading-tight mb-1.5 line-clamp-2">
              {service.name}
            </h3>
            {service.description && (
              <p className="text-sm text-gray-500 leading-relaxed line-clamp-2 mb-2">
                {service.description}
              </p>
            )}
            {/* Add-on Badge - Below service name */}
            {isAddOn && (
              <div className="mt-0.5">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  Add-on
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Spacer to push bottom section down */}
        <div className="flex-1"></div>

        {/* Service Details: full-bleed row so location icon can use right-3 and line up with the gear */}
        <div className="relative -mx-5 mt-auto border-t border-gray-100 px-5 pt-3">
          <div className="flex min-w-0 flex-wrap items-center gap-x-5 gap-y-2 pr-10">
            <div className="flex items-center gap-1.5 text-sm">
              <Tag className="w-4 h-4 text-gray-400 flex-shrink-0" />
              {isQuotable ? (
                <span className="font-semibold text-gray-900">Request a Quote</span>
              ) : (
                <span className="font-semibold text-gray-900">
                  {formatPrice(Number(service.price ?? 0))}
                </span>
              )}
            </div>

            <div className="flex items-center gap-1.5 text-sm">
              <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="font-medium text-gray-700">{formatDuration(service.durationMinutes)}</span>
            </div>

            {categoryName && (
              <div className="flex items-center gap-1.5 text-sm">
                <Layers className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="font-medium text-gray-700">{categoryName}</span>
              </div>
            )}
          </div>
          {serviceLocation && (
            <div
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400"
              title={serviceLocation === 'IN_STORE' ? 'In-store service' : serviceLocation === 'ON_SITE' ? 'On-site service' : 'Service location'}
            >
              {serviceLocation === 'IN_STORE' || serviceLocation === 'INSTORE' ? (
                <Store className="w-4 h-4" />
              ) : serviceLocation === 'ON_SITE' || serviceLocation === 'ONSITE' ? (
                <MapPin className="w-4 h-4" />
              ) : (
                <Store className="w-4 h-4" />
              )}
            </div>
          )}
        </div>

        {/* Assigned Professionals */}
        {professionalNames.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 mb-2">Assigned Professionals:</p>
            <div className="flex flex-wrap gap-1.5">
              {professionalNames.slice(0, 3).map((name, index) => (
                <span
                  key={index}
                  className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-100"
                >
                  {name}
                </span>
              ))}
              {professionalNames.length > 3 && (
                <span className="px-2.5 py-1 bg-gray-50 text-gray-600 text-xs font-medium rounded-full border border-gray-200">
                  +{professionalNames.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ServiceCard; 